import { supabase } from './supabase';
import type { Order, OrderItem, OrderStatus } from './database.types';

/**
 * Accès données + logique des commandes (US-501/US-502).
 * Porté depuis le legacy admin (fetch + Realtime INSERT/UPDATE + reconnexion auto),
 * mais sorti du monolithe : logique pure testable + I/O isolés.
 */

/** Libellé métier d'un statut (porté du legacy : accepted = "En cuisine"). */
export function statusLabel(status: OrderStatus): string {
  switch (status) {
    case 'recue':
      return '🔔 Nouvelle';
    case 'acceptee':
      return '✅ Acceptée';
    case 'en_cuisine':
      return '🍳 En cuisine';
    case 'prete':
      return '📦 Prête';
    case 'recuperee':
      return '🤝 Remise';
    case 'refusee':
      return '✖️ Refusée';
    case 'annulee':
      return '✖️ Annulée';
  }
}

/** Une commande est "active" (à traiter par la cuisine) si acceptée, en cuisine ou prête. */
export function isActiveOrder(o: Pick<Order, 'status'>): boolean {
  return o.status === 'acceptee' || o.status === 'en_cuisine' || o.status === 'prete';
}

/**
 * Commande payée EN LIGNE pas encore confirmée : à NE PAS préparer (ni sonnerie, ni cuisine,
 * ni acceptation) tant que Stripe n'a pas confirmé le paiement (payment_status = 'paid').
 * Les commandes "sur place" (onsite) ne sont jamais en attente ici (réglées au comptoir).
 */
export function isAwaitingOnlinePayment(o: Pick<Order, 'payment_method' | 'payment_status'>): boolean {
  return o.payment_method === 'online' && o.payment_status !== 'paid';
}

/**
 * Une commande doit-elle apparaître dans la file cuisine de la tablette ?
 * Parité legacy BB/FK : statut actif (recue→prete) ET pas un panier abandonné
 * (commande en ligne non payée = client parti au paiement). Les abandonnés restent
 * en base (contact = lead) mais ne polluent pas la tablette ; ils réapparaissent
 * dès que le paiement aboutit (payment_status === 'paid'). Pur (testable).
 */
export function isKitchenVisible(
  o: Pick<Order, 'status' | 'payment_method' | 'payment_status'>,
): boolean {
  return (
    ['recue', 'acceptee', 'en_cuisine', 'prete'].includes(o.status) &&
    !isAwaitingOnlinePayment(o)
  );
}

/** Tri pour la tablette : les plus récentes d'abord. */
export function sortByNewest(orders: Order[]): Order[] {
  return [...orders].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

/** Ligne brute de `order_items` : colonnes réelles de la base (name_snapshot/qty)
 *  ou forme déjà normalisée (name/quantity), selon la source. */
type RawOrderItem = {
  id: string;
  order_id: string;
  name?: string | null;
  name_snapshot?: string | null;
  quantity?: number | null;
  qty?: number | null;
  unit_price_cents?: number | null;
  options_json?: unknown;
};

/** Parse options_json (jsonb ou string) en [{group, values[]}] propre. Tolérant. */
function parseOptionLines(raw: unknown): { group: string; values: string[] }[] | undefined {
  let v = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return undefined; } }
  if (!Array.isArray(v)) return undefined;
  const out = v
    .map((g) => {
      const grp = (g && typeof g === 'object' ? (g as Record<string, unknown>) : {});
      const group = String(grp.group ?? '').trim();
      const values = Array.isArray(grp.values) ? grp.values.map((x) => String(x)).filter(Boolean) : [];
      return { group, values };
    })
    .filter((x) => x.group && x.values.length > 0);
  return out.length ? out : undefined;
}

/** Normalise une ligne `order_items` vers le type OrderItem (name/quantity),
 *  quelle que soit la source (vraie base = name_snapshot/qty, fixtures = name/quantity). */
export function normalizeOrderItem(raw: RawOrderItem): OrderItem {
  return {
    id: raw.id,
    order_id: raw.order_id,
    name: raw.name ?? raw.name_snapshot ?? '',
    quantity: raw.quantity ?? raw.qty ?? 1,
    unit_price_cents: raw.unit_price_cents ?? 0,
    options: parseOptionLines(raw.options_json),
  };
}

const ORDER_COLUMNS_BASE =
  'id, tenant_id, order_number, customer_name, status, payment_status, payment_method, ' +
  'pickup_mode, delivery_fee_cents, total_cents, pickup_date, pickup_time, accepted_at, ready_at, picked_up_at, ' +
  'refusal_reason, customer_note, created_at';

/** Colonnes file unifiée (Chantier 1). Ajoutées en SELECT défensif : si la migration
 *  SQL 37 n'est pas encore appliquée, on retombe sur les colonnes de base (zéro régression). */
const ORDER_COLUMNS = `${ORDER_COLUMNS_BASE}, source, external_ref`;

/** Une commande provient-elle d'Uber Eats ? (absence de source = YumLink, rétro-compat). */
export function isUberOrder(o: Pick<Order, 'source'>): boolean {
  return (o.source ?? 'yumlink') === 'uber';
}

/**
 * Badge d'origine pour la tablette (file unifiée YumLink + Uber).
 * Le canal propre s'affiche au **nom du restaurant** (white-label) si fourni,
 * sinon repli « YumLink » ; Uber Eats reste distinct.
 */
export function orderSourceBadge(
  o: Pick<Order, 'source'>,
  restaurantName?: string | null,
): { label: string; cls: string } {
  if (isUberOrder(o)) return { label: '🛵 Uber Eats', cls: 'bg-black text-white' };
  const name = (restaurantName ?? '').trim();
  return { label: `🟢 ${name || 'YumLink'}`, cls: 'bg-emerald-100 text-emerald-800' };
}

/** Erreur PostgREST « colonne inexistante » (42703) : la migration n'est pas encore passée. */
export function isMissingColumnError(err: { code?: string; message?: string } | null): boolean {
  if (!err) return false;
  if (err.code === '42703') return true;
  const m = (err.message ?? '').toLowerCase();
  return m.includes('column') && m.includes('does not exist');
}

/** Libellé métier d'un statut de paiement (porté du legacy PAY_STATUS). */
export function paymentStatusLabel(status: string | null): string {
  switch (status) {
    case 'paid':
      return '💳 Payée';
    case 'pending':
      return '⏳ En attente';
    case 'refunded':
      return '↩️ Remboursée';
    case 'failed':
      return '⚠️ Échec';
    case 'onsite_pending':
      return '💵 À encaisser';
    case 'onsite_collected':
      return '✅ Encaissée';
    default:
      return '—';
  }
}

/** Transitions de statut autorisées (flux cuisine). */
export const NEXT_STATUS: Partial<Record<OrderStatus, { status: OrderStatus; label: string }>> = {
  acceptee: { status: 'en_cuisine', label: 'Mettre en cuisine' },
  en_cuisine: { status: 'prete', label: 'Marquer prête' },
  prete: { status: 'recuperee', label: 'Marquer remise' },
};

/**
 * Statut → event d'email client (Edge `send-order-email`). Pur & testable.
 * On notifie le client à la PRISE EN CHARGE (acceptée) et quand la commande est PRÊTE,
 * et on l'informe aussi d'un refus. Les autres transitions n'envoient rien.
 */
export function statusEmailEvent(status: OrderStatus): 'accepted' | 'ready' | 'refused' | null {
  if (status === 'acceptee') return 'accepted';
  if (status === 'prete') return 'ready';
  if (status === 'refusee') return 'refused';
  return null;
}

/**
 * Statut → event WhatsApp (Edge `send-whatsapp`, templates Meta approuvés). Pur & testable.
 * Meta ne fournit que placed/accepted/ready/review → on notifie à la PRISE EN CHARGE
 * (acceptée) et quand la commande est PRÊTE (pas de template « refus » côté WhatsApp).
 */
export function statusWhatsappEvent(status: OrderStatus): 'accepted' | 'ready' | null {
  if (status === 'acceptee') return 'accepted';
  if (status === 'prete') return 'ready';
  return null;
}

/**
 * Notifie le client par email selon le nouveau statut (best-effort, NON bloquant).
 * S'appuie sur l'Edge Function `send-order-email` (Resend, brandée au resto) qui
 * ignore d'elle-même les commandes sans `customer_email`. Actif pour TOUS les tenants
 * (aucune config par resto) → chaque nouveau client en bénéficie d'office.
 */
export async function notifyCustomerEmail(orderId: string, status: OrderStatus): Promise<void> {
  const event = statusEmailEvent(status);
  if (!event) return;
  try {
    await supabase.functions.invoke('send-order-email', { body: { order_id: orderId, event } });
  } catch (e) {
    console.warn('[orders] email client (non bloquant) échoué:', (e as Error).message);
  }
}

/**
 * Notifie le client par WhatsApp selon le statut (best-effort, NON bloquant).
 * S'appuie sur l'Edge `send-whatsapp` (Meta Cloud API) qui ignore les commandes sans
 * `customer_phone` valide. Ne fait rien tant que les secrets Meta / templates ne sont
 * pas configurés (l'Edge renvoie « skipped »). Actif pour tous les tenants.
 */
export async function notifyCustomerWhatsapp(orderId: string, status: OrderStatus): Promise<void> {
  const event = statusWhatsappEvent(status);
  if (!event) return;
  try {
    await supabase.functions.invoke('send-whatsapp', { body: { order_id: orderId, event } });
  } catch (e) {
    console.warn('[orders] WhatsApp client (non bloquant) échoué:', (e as Error).message);
  }
}

/**
 * Met à jour le statut d'une commande + l'horodatage métier correspondant.
 * Renvoie true si OK. I/O isolée (logique de transition restée pure ailleurs).
 * Envoie aussi, best-effort, l'email client (prise en charge / prête / refus).
 */
export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus,
  refusalReason?: string,
): Promise<boolean> {
  const patch: Record<string, unknown> = { status };
  const now = new Date().toISOString();
  if (status === 'acceptee') patch.accepted_at = now;
  if (status === 'prete') patch.ready_at = now;
  if (status === 'recuperee') patch.picked_up_at = now;
  if (status === 'refusee') patch.refusal_reason = refusalReason ?? 'Refusée par le restaurant';
  const { error } = await supabase.from('orders').update(patch).eq('id', orderId);
  if (error) {
    console.warn('[orders] maj statut échouée:', error.message);
    return false;
  }
  // Notifications client (prise en charge / prête / refus) — best-effort, ne bloquent pas l'UI.
  void notifyCustomerEmail(orderId, status);
  void notifyCustomerWhatsapp(orderId, status);
  return true;
}

/**
 * Annule une commande en **gardant la trace** (statut → `annulee`) — parité legacy.
 * La commande reste visible dans « Toutes » / l'historique et l'export.
 */
export async function cancelOrder(orderId: string, reason?: string): Promise<boolean> {
  return updateOrderStatus(orderId, 'annulee', reason);
}

/**
 * Supprime **définitivement** une commande (lignes puis en-tête) — parité legacy.
 * Irréversible : à réserver aux commandes de test / doublons. Le composant confirme avant.
 */
export async function deleteOrder(orderId: string): Promise<{ ok: boolean; error?: string }> {
  await supabase.from('order_items').delete().eq('order_id', orderId);
  const { error } = await supabase.from('orders').delete().eq('id', orderId);
  if (error) {
    console.warn('[orders] suppression échouée:', error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/** Marque une commande comme encaissée sur place (payment_status → onsite_collected). */
export async function markOrderPaid(orderId: string): Promise<boolean> {
  const { error } = await supabase
    .from('orders')
    .update({ payment_status: 'onsite_collected' })
    .eq('id', orderId);
  if (error) {
    console.warn('[orders] encaissement échoué:', error.message);
    return false;
  }
  return true;
}

/** Charge les dernières commandes d'un tenant (+ leurs lignes). */
export async function loadOrders(
  tenantId: string,
  limit = 200,
): Promise<{ orders: Order[]; itemsByOrder: Record<string, OrderItem[]> }> {
  const runQuery = (cols: string) =>
    supabase
      .from('orders')
      .select(cols)
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(limit);

  let res = await runQuery(ORDER_COLUMNS);
  // Repli défensif : si la migration SQL 37 (source/external_ref) n'est pas encore
  // appliquée, on recharge avec les colonnes de base -> aucune régression tablette.
  if (res.error && isMissingColumnError(res.error)) {
    res = await runQuery(ORDER_COLUMNS_BASE);
  }

  if (res.error) {
    console.warn('[orders] fetch échoué:', res.error.message);
    return { orders: [], itemsByOrder: {} };
  }

  const list = (res.data as unknown as Order[]) ?? [];
  const ids = list.map((o) => o.id);
  const itemsByOrder: Record<string, OrderItem[]> = {};

  if (ids.length) {
    const { data: items } = await supabase.from('order_items').select('*').in('order_id', ids);
    (items as unknown as RawOrderItem[] | null)?.forEach((raw) => {
      const it = normalizeOrderItem(raw);
      (itemsByOrder[it.order_id] ??= []).push(it);
    });
  }

  return { orders: list, itemsByOrder };
}

export interface OrderSubscription {
  unsubscribe: () => void;
}

/**
 * Abonnement Realtime aux commandes d'un tenant (INSERT + UPDATE), avec reconnexion auto.
 * Porté du legacy (channel "orders-tenant-<id>"), simplifié et typé.
 */
export function subscribeOrders(
  tenantId: string,
  handlers: { onInsert: (o: Order) => void; onUpdate: (o: Order) => void },
): OrderSubscription {
  let channel = supabase.channel('orders-tenant-' + tenantId);
  let closed = false;

  const setup = () => {
    channel
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders', filter: 'tenant_id=eq.' + tenantId },
        (payload: { new: Order }) => handlers.onInsert(payload.new),
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders', filter: 'tenant_id=eq.' + tenantId },
        (payload: { new: Order }) => handlers.onUpdate(payload.new),
      )
      .subscribe((status: string) => {
        if (
          !closed &&
          (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED')
        ) {
          setTimeout(() => {
            if (closed) return;
            supabase.removeChannel(channel);
            channel = supabase.channel('orders-tenant-' + tenantId);
            setup();
          }, 3000);
        }
      });
  };
  setup();

  return {
    unsubscribe: () => {
      closed = true;
      supabase.removeChannel(channel);
    },
  };
}
